/**
 * The monthly inputs, typed in: index levels that move article costs, and the market situation
 * per territory. Each save is audited and moves live prices from the next quote.
 */
import { useCallback, useEffect, useState } from 'react'
import { cx, ErrorBox, Panel, Spinner } from '../../components/ui'
import { api, errorMessage } from '../../lib/api'
import { monthLabel } from '../../lib/format'
import { localName, useT } from '../../lib/i18n'

interface IndexLevelsView {
  month: string
  months: string[]
  indices: { name: string; articles: number }[]
  values: { index: string; month: string; value: number }[]
  forwards: { index: string; unit: string | null; next3: number | null; m3to6: number | null; m6to12: number | null; source: string | null }[]
}

interface MarketDriver {
  id: string
  name: string
  nameDe: string | null
  levels: string[]
  levelLabels: string[] | null
  levelLabelsDe: string[] | null
  anchorLow: string | null
  anchorHigh: string | null
}
interface MarketView {
  month: string
  months: string[]
  territories: string[]
  drivers: MarketDriver[]
  ratings: { driverId: string; month: string; territory: string; level: string }[]
}

function SaveButtons({ dirty, busy, saved, onSave, onDiscard }: { dirty: boolean; busy: boolean; saved: boolean; onSave: () => void; onDiscard: () => void }) {
  const { t } = useT()
  return (
    <div className="flex items-center gap-2">
      {saved && !dirty && <span className="text-xs text-emerald-600">{t('Saved. Applies from the next price.')}</span>}
      {dirty && (
        <button type="button" className="btn btn-sm" disabled={busy} onClick={onDiscard}>
          {t('Discard changes')}
        </button>
      )}
      <button type="button" className="btn btn-sm btn-primary" disabled={!dirty || busy} onClick={onSave}>
        {busy ? t('Saving…') : t('Save')}
      </button>
    </div>
  )
}

const num = (s: string) => Number(s.trim().replace(/\s/g, '').replace(',', '.'))

type ForwardField = 'unit' | 'next3' | 'm3to6' | 'm6to12' | 'source'
const FORWARD_NUM: ForwardField[] = ['next3', 'm3to6', 'm6to12']

/**
 * Indices down. Left: the actual levels of the last months (the base an article's cost is
 * measured against). Right: what each index is expected to average 0–3, 3–6 and 6–12 months
 * ahead; a quote takes the span of its contract term.
 */
export function IndexLevelsEditor() {
  const { t, lang } = useT()
  const [view, setView] = useState<IndexLevelsView | null>(null)
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [added, setAdded] = useState<string[]>([])
  const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(() => api.get<IndexLevelsView>('/api/admin/index-levels').then(setView).catch((e) => setError(errorMessage(e))), [])
  useEffect(() => {
    load()
  }, [load])
  if (!view) return error ? <ErrorBox>{error}</ErrorBox> : <Spinner />

  const months = view.months.slice(-3)
  const stored = new Map(view.values.map((v) => [`${v.index}|${v.month}`, v.value]))
  const fwd = new Map(view.forwards.map((f) => [f.index, f]))
  const indices = [...view.indices, ...added.filter((a) => !view.indices.some((i) => i.name === a)).map((name) => ({ name, articles: 0 }))]
  const fmt = (n: number) => n.toLocaleString(lang === 'de' ? 'de-AT' : 'en-GB', { maximumFractionDigits: 2 })
  const badNum = (v: string) => v.trim() !== '' && !(num(v) > 0)
  const invalid = Object.entries(edits).some(([k, v]) => (k.startsWith('L|') || FORWARD_NUM.some((f) => k.endsWith(`|${f}`))) && badNum(v))
  const dirty = Object.keys(edits).length > 0
  const storedForward = (index: string, f: ForwardField): string => {
    const r = fwd.get(index)
    const v = r?.[f]
    if (v === null || v === undefined) return ''
    return typeof v === 'number' ? fmt(v) : v
  }
  const setCell = (k: string, nv: string, original: string) => {
    setSaved(false)
    setEdits((x) => {
      const next = { ...x }
      if (nv.trim() === original.trim()) delete next[k]
      else next[k] = nv
      return next
    })
  }
  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const rows = Object.entries(edits)
        .filter(([k]) => k.startsWith('L|'))
        .map(([k, v]) => {
          const [, index, month] = k.split('|') as [string, string, string]
          return { index, month, value: v.trim() === '' ? null : num(v) }
        })
      const touched = [...new Set(Object.keys(edits).filter((k) => k.startsWith('F|')).map((k) => k.split('|')[1]!))]
      const forwards = touched.map((index) => {
        const cur = (f: ForwardField) => edits[`F|${index}|${f}`] ?? storedForward(index, f)
        const n = (f: ForwardField) => (cur(f).trim() === '' ? null : num(cur(f)))
        return { index, unit: cur('unit') || null, next3: n('next3'), m3to6: n('m3to6'), m6to12: n('m6to12'), source: cur('source') || null }
      })
      setView(await api.put<IndexLevelsView>('/api/admin/index-levels', { rows, forwards }))
      setEdits({})
      setAdded([])
      setSaved(true)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }
  const span = (from: number, to: number) => `${monthLabel(addMonthsLocal(view.month, from))} – ${monthLabel(addMonthsLocal(view.month, to))}`
  const FWD_HEAD: { f: ForwardField; label: string; sub: string }[] = [
    { f: 'next3', label: t('0–3 months'), sub: span(1, 3) },
    { f: 'm3to6', label: t('3–6 months'), sub: span(4, 6) },
    { f: 'm6to12', label: t('6–12 months'), sub: span(7, 12) },
  ]

  return (
    <Panel
      title={t('Index levels')}
      right={
        <SaveButtons
          dirty={dirty}
          busy={busy || invalid}
          saved={saved}
          onSave={save}
          onDiscard={() => {
            setEdits({})
            setAdded([])
          }}
        />
      }
    >
      {error && <div className="mb-3"><ErrorBox>{error}</ErrorBox></div>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left align-bottom">
              <th className="label pb-2 pr-3 font-medium">{t('Index')}</th>
              <th className="label pb-2 pr-3 font-medium">{t('Unit')}</th>
              <th className="label pb-2 pr-3 text-right font-medium">{t('Articles')}</th>
              {months.map((m) => (
                <th key={m} className={cx('label pb-2 pl-2 text-right font-medium', m === view.month && 'text-sky-500')}>
                  {monthLabel(m)}
                </th>
              ))}
              {FWD_HEAD.map((h, i) => (
                <th key={h.f} className={cx('label whitespace-nowrap pb-2 pl-2 text-right font-medium', i === 0 && 'border-l border-slate-800 pl-3')}>
                  <div>{h.label}</div>
                  <div className="font-normal normal-case tracking-normal text-slate-500">{h.sub}</div>
                </th>
              ))}
              <th className="label pb-2 pl-3 font-medium">{t('Source')}</th>
            </tr>
          </thead>
          <tbody>
            {indices.map((ix) => (
              <tr key={ix.name} className="border-t border-slate-800">
                <td className="py-1.5 pr-3 font-mono text-[13px] text-slate-200">{ix.name}</td>
                <td className="py-1.5 pr-3">
                  <input
                    className={cx('field !w-[4.5rem] !px-2', edits[`F|${ix.name}|unit`] !== undefined && 'border-sky-500')}
                    value={edits[`F|${ix.name}|unit`] ?? storedForward(ix.name, 'unit')}
                    placeholder="EUR/t"
                    onChange={(e) => setCell(`F|${ix.name}|unit`, e.target.value, storedForward(ix.name, 'unit'))}
                  />
                </td>
                <td className="py-1.5 pr-3 text-right tabular text-slate-500">{ix.articles || '–'}</td>
                {months.map((m) => {
                  const k = `L|${ix.name}|${m}`
                  const v = stored.get(`${ix.name}|${m}`)
                  const orig = v !== undefined ? fmt(v) : ''
                  const missing = m === view.month && v === undefined && edits[k] === undefined && ix.articles > 0
                  return (
                    <td key={m} className="py-1.5 pl-2">
                      <input
                        className={cx('field !w-[5.25rem] !px-2 text-right tabular', edits[k] !== undefined && 'border-sky-500', edits[k] !== undefined && badNum(edits[k]!) && 'border-rose-500', missing && 'border-amber-500')}
                        inputMode="decimal"
                        value={edits[k] ?? orig}
                        placeholder={missing ? t('missing') : ''}
                        onChange={(e) => setCell(k, e.target.value, orig)}
                      />
                    </td>
                  )
                })}
                {FWD_HEAD.map((h, i) => {
                  const k = `F|${ix.name}|${h.f}`
                  const orig = storedForward(ix.name, h.f)
                  return (
                    <td key={h.f} className={cx('py-1.5 pl-2', i === 0 && 'border-l border-slate-800 pl-3')}>
                      <input
                        className={cx('field !w-[5.25rem] !px-2 text-right tabular', edits[k] !== undefined && 'border-sky-500', edits[k] !== undefined && badNum(edits[k]!) && 'border-rose-500')}
                        inputMode="decimal"
                        value={edits[k] ?? orig}
                        placeholder="–"
                        onChange={(e) => setCell(k, e.target.value, orig)}
                      />
                    </td>
                  )
                })}
                <td className="py-1.5 pl-3">
                  <input
                    className={cx('field !w-28', edits[`F|${ix.name}|source`] !== undefined && 'border-sky-500')}
                    value={edits[`F|${ix.name}|source`] ?? storedForward(ix.name, 'source')}
                    placeholder={t('e.g. exchange settlement')}
                    onChange={(e) => setCell(`F|${ix.name}|source`, e.target.value, storedForward(ix.name, 'source'))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <input className="field !w-56 font-mono" placeholder={t('New index, e.g. STEEL')} value={newName} onChange={(e) => setNewName(e.target.value.toUpperCase().replace(/\s+/g, '_'))} />
        <button
          type="button"
          className="btn btn-sm whitespace-nowrap"
          disabled={!newName.trim() || indices.some((i) => i.name === newName.trim())}
          onClick={() => {
            setAdded((a) => [...a, newName.trim()])
            setNewName('')
          }}
        >
          {t('Add index')}
        </button>
      </div>
    </Panel>
  )
}

const addMonthsLocal = (m: string, n: number) => {
  const [y, mm] = m.split('-').map(Number) as [number, number]
  const d = y * 12 + (mm - 1) + n
  return `${Math.floor(d / 12)}-${String((d % 12) + 1).padStart(2, '0')}`
}

/** Per market driver: territories down (the "all territories" row first), months across. */
export function MarketEditor() {
  const { t, lang } = useT()
  const [view, setView] = useState<MarketView | null>(null)
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(() => api.get<MarketView>('/api/admin/market-ratings').then(setView).catch((e) => setError(errorMessage(e))), [])
  useEffect(() => {
    load()
  }, [load])
  if (!view) return error ? <ErrorBox>{error}</ErrorBox> : <Spinner />

  const stored = new Map(view.ratings.map((r) => [`${r.driverId}|${r.territory}|${r.month}`, r.level]))
  const dirty = Object.keys(edits).length > 0
  const value = (k: string) => edits[k] ?? stored.get(k) ?? ''
  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const changes = Object.entries(edits).map(([k, level]) => {
        const [driverId, territory, month] = k.split('|') as [string, string, string]
        return { driverId, territory, month, level: level === '' ? null : level }
      })
      setView(await api.put<MarketView>('/api/admin/market-ratings', { changes }))
      setEdits({})
      setSaved(true)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }
  const rows = ['*', ...view.territories]

  return (
    <>
      {view.drivers.map((d) => {
        const labels = lang === 'de' && d.levelLabelsDe ? d.levelLabelsDe : d.levelLabels
        const label = (lv: string) => {
          const i = d.levels.indexOf(lv)
          return labels?.[i] ?? lv
        }
        return (
          <Panel
            key={d.id}
            title={localName(lang, d.name, d.nameDe)}
            right={<SaveButtons dirty={dirty} busy={busy} saved={saved} onSave={save} onDiscard={() => setEdits({})} />}
          >
            {error && <div className="mb-3"><ErrorBox>{error}</ErrorBox></div>}
            {(d.anchorLow || d.anchorHigh) && (
              <p className="mb-3 text-[12px] text-slate-500">
                {d.levels[0]}: {d.anchorLow} · {d.levels[d.levels.length - 1]}: {d.anchorHigh}
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left">
                    <th className="label pb-2 pr-3 font-medium">{t('Territory')}</th>
                    {view.months.map((m) => (
                      <th key={m} className={cx('label pb-2 pl-2 font-medium', m === view.month && 'text-sky-500')}>
                        {monthLabel(m)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((terr) => (
                    <tr key={terr} className="border-t border-slate-800">
                      <td className={cx('py-1.5 pr-3', terr === '*' ? 'font-medium text-slate-100' : 'text-slate-300')}>{terr === '*' ? t('All territories') : terr}</td>
                      {view.months.map((m) => {
                        const k = `${d.id}|${terr}|${m}`
                        const v = value(k)
                        const fallback = terr !== '*' ? value(`${d.id}|*|${m}`) : ''
                        const uncovered = !v && !fallback
                        return (
                          <td key={m} className="py-1.5 pl-2">
                            <select
                              className={cx(
                                'field w-24',
                                edits[k] !== undefined && 'border-sky-500',
                                uncovered && m >= view.month && m <= view.months[3]! && 'border-amber-500',
                                !v && 'text-slate-500',
                              )}
                              value={v}
                              title={!v && fallback ? `${t('All territories')}: ${label(fallback)}` : undefined}
                              onChange={(e) => {
                                setSaved(false)
                                const nv = e.target.value
                                setEdits((x) => {
                                  const next = { ...x }
                                  if (nv === (stored.get(k) ?? '')) delete next[k]
                                  else next[k] = nv
                                  return next
                                })
                              }}
                            >
                              <option value="">{terr !== '*' && fallback ? `(${label(fallback)})` : '–'}</option>
                              {d.levels.map((lv) => (
                                <option key={lv} value={lv}>
                                  {label(lv)}
                                </option>
                              ))}
                            </select>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        )
      })}
    </>
  )
}
